import { useTranslation } from 'react-i18next';
import { Box, Divider, Grid, Table, Text } from '@mantine/core';
import { mapRoomTypeName } from '@utils/mappers';
import { priceToDecimal } from '@utils/price';

import {
  BookingPartecipant,
  BookingRooms,
} from '@interfaces/booking.interface';

interface PartecipantInfoProps {
  partecipant: BookingPartecipant;
  rooms: BookingRooms[];
  partecipantCosts: {
    partecipantId: number;
    roomCost: number;
    extrasCosts: number;
    holidayCost: number;
    ensurancesCosts: number;
    couponValue?: number;
  };
}

export default function PartecipantInfo({
  partecipant,
  rooms,
  partecipantCosts,
}: PartecipantInfoProps) {
  const { t } = useTranslation();

  return (
    <>
      <Grid gutter="xs" c="#444">
        <Grid.Col span={{ md: 6 }}>
          <Text>
            <Text span fw="bold">
              {t('bookings.participant.info.taxCode')}
            </Text>{' '}
            {partecipant.taxCode ?? '-'}
          </Text>
          <Text>
            <Text span fw="bold">
              {t('bookings.participant.info.gender')}
            </Text>{' '}
            {partecipant.gender === 'female'
              ? t('bookings.participant.info.genderFemale')
              : t('bookings.participant.info.genderMale')}
          </Text>
          <Text>
            <Text span fw="bold">
              {t('bookings.participant.info.birthDate')}
            </Text>{' '}
            {partecipant.birthDate
              ? new Date(partecipant.birthDate).toLocaleDateString()
              : '-'}
          </Text>
          <Text>
            <Text span fw="bold">
              {t('bookings.participant.info.birthCity')}
            </Text>{' '}
            {partecipant.birthCity || '-'}
          </Text>
          <Text>
            <Text span fw="bold">
              {t('bookings.participant.info.height')}
            </Text>{' '}
            {partecipant.height
              ? `${partecipant.height} ${t('bookings.participant.info.heightUnit')}`
              : '-'}
          </Text>
          <Text>
            <Text span fw="bold">
              {t('bookings.participant.info.phone')}
            </Text>{' '}
            {partecipant.phone ?? '-'}
          </Text>
        </Grid.Col>
        <Grid.Col span={{ md: 6 }}>
          <Text>
            <Text span fw="bold">
              {t('bookings.participant.info.country')}
            </Text>{' '}
            {partecipant.country || '-'}
          </Text>
          <Text>
            <Text span fw="bold">
              {t('bookings.participant.info.province')}
            </Text>{' '}
            {partecipant.province?.toLocaleUpperCase() || '-'}
          </Text>
          <Text>
            <Text span fw="bold">
              {t('bookings.participant.info.city')}
            </Text>{' '}
            {partecipant.city || '-'}
          </Text>
          <Text>
            <Text span fw="bold">
              {t('bookings.participant.info.zip')}
            </Text>{' '}
            {partecipant.zip || '-'}
          </Text>
          <Text>
            <Text span fw="bold">
              {t('bookings.participant.info.address')}
            </Text>{' '}
            {partecipant.address || '-'}
          </Text>
        </Grid.Col>
        {partecipant.notes && (
          <Grid.Col span={12}>
            <Text span fw="bold">
              {t('bookings.participant.info.notes')}
            </Text>{' '}
            <Text>{partecipant.notes}</Text>
          </Grid.Col>
        )}
        {partecipant.ensuranceNumber && (
          <Grid.Col span={12}>
            <Text span fw="bold">
              {t('bookings.participant.info.ensuranceNumber')}
            </Text>{' '}
            <Text>{partecipant.ensuranceNumber}</Text>
          </Grid.Col>
        )}
      </Grid>

      <Box mt="md" c="#444">
        <Text fw="bold" span>
          {t('bookings.participant.info.room')}{' '}
        </Text>
        <Text span>
          {mapRoomTypeName(
            rooms.find((r) => r.id === partecipant.bookingRoomId)!.typeId,
          )}
        </Text>

        <Table
          mt="md"
          w="min-content"
          cellPadding={0}
          cellSpacing={0}
          withRowBorders={false}
          styles={{ td: { padding: 0 } }}
        >
          <Table.Tbody>
            <Table.Tr>
              <Table.Td fs="italic" style={{ whiteSpace: 'nowrap' }}>
                {t('bookings.participant.info.holidayCost')}
              </Table.Td>
              <Table.Td style={{ whiteSpace: 'nowrap', paddingLeft: '1rem' }}>
                € {priceToDecimal(partecipantCosts.holidayCost)}
              </Table.Td>
            </Table.Tr>
            {partecipantCosts.roomCost > 0 && (
              <Table.Tr>
                <Table.Td fs="italic" style={{ whiteSpace: 'nowrap' }}>
                  {t('bookings.participant.info.roomWithSupplement')}
                </Table.Td>
                <Table.Td style={{ whiteSpace: 'nowrap', paddingLeft: '1rem' }}>
                  € {priceToDecimal(partecipantCosts.roomCost)}
                </Table.Td>
              </Table.Tr>
            )}
            {partecipantCosts.extrasCosts > 0 && (
              <Table.Tr>
                <Table.Td fs="italic" style={{ whiteSpace: 'nowrap' }}>
                  {t('bookings.participant.info.extras')}
                </Table.Td>
                <Table.Td style={{ whiteSpace: 'nowrap', paddingLeft: '1rem' }}>
                  € {priceToDecimal(partecipantCosts.extrasCosts)}
                </Table.Td>
              </Table.Tr>
            )}
            {partecipantCosts.couponValue !== undefined &&
              partecipantCosts.couponValue !== 0 && (
                <Table.Tr>
                  <Table.Td fs="italic" style={{ whiteSpace: 'nowrap' }}>
                    {t('bookings.participant.info.discount')}
                  </Table.Td>
                  <Table.Td
                    style={{ whiteSpace: 'nowrap', paddingLeft: '1rem' }}
                  >
                    - € {priceToDecimal(partecipantCosts.couponValue)}
                  </Table.Td>
                </Table.Tr>
              )}
            {partecipantCosts.ensurancesCosts > 0 && (
              <Table.Tr>
                <Table.Td fs="italic" style={{ whiteSpace: 'nowrap' }}>
                  {t('bookings.participant.info.insurance')}
                </Table.Td>
                <Table.Td style={{ whiteSpace: 'nowrap', paddingLeft: '1rem' }}>
                  € {priceToDecimal(partecipantCosts.ensurancesCosts)}
                </Table.Td>
              </Table.Tr>
            )}
            <Table.Tr>
              <Table.Td fs="italic" style={{ whiteSpace: 'nowrap' }}>
                {t('bookings.participant.info.total')}
              </Table.Td>
              <Table.Td style={{ whiteSpace: 'nowrap', paddingLeft: '1rem' }}>
                €{' '}
                {priceToDecimal(
                  partecipantCosts.holidayCost +
                    partecipantCosts.extrasCosts +
                    partecipantCosts.roomCost +
                    partecipantCosts.ensurancesCosts,
                )}
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Box>
      <Divider my="md" />
    </>
  );
}
